"use client";

import { startTransition, useEffect, useState } from "react";
import { useTheme } from "next-themes";
import Image from "next/image";
import Link from "next/link";
import { Button } from "../ui/button";
import { cn } from "@/lib/utils";
import { usePathname } from "next/navigation";
import { BellIcon, CircleStackIcon, ShoppingBagIcon, UserCircleIcon } from "@heroicons/react/24/outline";
import { signOut, useSession } from "next-auth/react";
import ShoppingCartIcon from "../atoms/dynamic-icons/shopping-cart";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import LogoutIcon from "../atoms/dynamic-icons/logout";
import SearchBar from "./search-bar";
import PackageBoxIcon from "../atoms/dynamic-icons/package-box";
import MobileNav from "./mobile-nav";
import { FEDERAL_WORKER_USERTYPE } from "@/lib/constants";
import { useCart } from "@/hooks/useCart";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import AnimatedLink from "../ui/animated-link";
import { User } from "lucide-react";
import { getMarketplaceUrl } from "@/lib/marketplace-utils";

export default function NavBar() {
  const pathname = usePathname();
  const isMarketplaceEnabled = process.env.NEXT_PUBLIC_MARKETPLACE_FEATURE_FLAG === "true";

  const isHomePage = pathname === "/" || !isMarketplaceEnabled;
  const isMarketplacePage =
    pathname.includes("/shop") ||
    pathname.includes("/cart") ||
    pathname.includes("/category") ||
    pathname.includes("/search");
  const isAuthPages =
    pathname.includes("/login") ||
    pathname.includes("/register") ||
    pathname.includes("/forgot-password") ||
    pathname.includes("/become-sales-agent");
  const changeToshopIcon =
    pathname.includes("/cart") ||
    pathname.includes("/profile-and-settings") ||
    pathname.includes("/orders") ||
    pathname.includes("/credits") ||
    pathname.includes("/notifications");
  const menu = isHomePage
    ? [
        {
          label: "Payment Plans",
          href: "/#how-will-i-pay",
          disabled: false,
        },
        {
          label: "Privacy and Terms",
          href: "/terms-and-conditions",
          disabled: false,
        },
        {
          label: "FAQ",
          href: "/#faq",
          disabled: false,
        },
      ]
    : [
        {
          label: "Profile & Settings",
          href: "/profile-and-settings",
          icon: UserCircleIcon,
          disabled: false,
        },
        {
          label: "My Orders",
          href: "/orders",
          icon: PackageBoxIcon,
          disabled: false,
        },
        {
          label: "My Credits",
          href: "/credits",
          icon: CircleStackIcon,
          disabled: false,
        },
        {
          label: "Notifications",
          href: "/notifications",
          icon: BellIcon,
          disabled: false,
        },
      ];
  const { theme } = useTheme();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    startTransition(() => {
      setMounted(true);
    });
  }, []);

  const [isOpen, setIsOpen] = useState(false);
  const session = useSession();
  console.log(session);
  console.log(session.data?.user);
  const { totalItems } = useCart();
  const queryClient = useQueryClient();
  const handleLogout = async () => {
    toast.promise(
      (async () => {
        await signOut({
          redirect: true,
          callbackUrl: `/login/${FEDERAL_WORKER_USERTYPE}`,
        });
        localStorage.clear();
        queryClient.clear();
      })(),
      {
        loading: "Logging out...",
        success: "Logged out successfully",
        error: "Failed to log out. Please try again.",
      }
    );
  };

  return (
    <div
      className={cn(
        "z-50 flex flex-wrap items-center justify-between bg-transparent px-4 transition-all duration-200 ease-in-out md:right-10 md:left-10 2xl:h-14 2xl:px-12",
        isHomePage
          ? "md:bg-bg-tetiary absolute top-2 right-4 left-2 h-14 rounded-full md:px-10"
          : "bg-link-primary sticky top-0 px-4 py-2 shadow-sm md:px-6 lg:px-10 xl:px-14 dark:shadow-white/10",
        isAuthPages && "hidden"
      )}
    >
      <div className='flex items-center gap-2'>
        <MobileNav isOpen={isOpen} setIsOpen={setIsOpen} menu={menu} pathname={pathname} isHomePage={isHomePage} />
        <Link
          href='/'
          className={cn(
            "text-link-primary relative left-0 flex translate-x-0 items-center font-medium",
            !isHomePage && "text-text-title-inv dark:text-text-title hidden md:flex",
            isMarketplacePage && "flex"
          )}
        >
          <Image
            src={
              mounted && theme === "dark"
                ? "/assets/icons/logo-white.svg"
                : mounted && isHomePage && theme !== "dark"
                  ? "/assets/icons/logo-light.svg"
                  : "/assets/icons/logo-white.svg"
            }
            alt='Onyx Logo'
            width={24}
            height={24}
            className='mr-2 inline-flex size-5 md:size-6 2xl:size-8'
          />
          Onyx <span className='text-text-title-inv dark:text-text-title'>.</span>
        </Link>
      </div>
      {isHomePage ? (
        <div
          className={cn(
            "hidden items-center gap-4 md:flex md:gap-10"
            // isAuthPages &&
            //   "md:bg-bg-tetiary absolute left-1/2 min-w-2/3 flex-1 -translate-x-1/2 justify-center rounded-full px-8 md:h-full md:gap-7 lg:min-w-fit"
          )}
        >
          {menu.map((item) => (
            <AnimatedLink
              key={item.label}
              href={item.href}
              showHoverCircle={false}
              onClick={(e) => {
                if (item.href.startsWith("#") && e) {
                  e.preventDefault();
                  const targetId = item.href.substring(1);
                  const targetElement = document.getElementById(targetId);
                  if (targetElement) {
                    targetElement.scrollIntoView({ behavior: "smooth", block: "start" });
                  }
                }
              }}
              className={cn(
                "hover:text-link-primary text-link-muted text-xs font-semibold duration-300 ease-in-out md:text-sm 2xl:text-base",
                pathname === item.href && "text-link-primary"
              )}
            >
              {item.label}
            </AnimatedLink>
          ))}
        </div>
      ) : (
        <div className='hidden'></div>
      )}
      <div className={cn("flex h-full items-center gap-2 px-0 md:gap-6", !isHomePage && "min-w-fit md:min-w-1/2")}>
        {!isHomePage ? (
          <div className='hidden w-full md:flex'>
            <SearchBar />
          </div>
        ) : null}
        {session.data?.user.token ? (
          <div className={cn("flex h-full items-center gap-2 px-0 md:gap-6")}>
            {changeToshopIcon ? (
              <Link href='/shop' className='relative' title='Shop'>
                <ShoppingBagIcon className='text-icon-title-inv size-6 cursor-pointer stroke-[2px]' />
              </Link>
            ) : (
              <Link href='/cart' className='relative' title='Shopping Cart'>
                <ShoppingCartIcon className='text-icon-title-inv size-5 cursor-pointer stroke-[2.5px]' />
                {totalItems > 0 && (
                  <span className='text-text-title-inv absolute -top-3 -right-3 flex size-4.5 items-center justify-center rounded-full bg-red-500 text-[8px] font-light'>
                    {totalItems > 99 ? "99+" : totalItems}
                  </span>
                )}
              </Link>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger className='min-w-fit'>
                <div className='text-text-title border-border-primmary hidden h-full w-fit min-w-fit cursor-pointer items-center justify-center rounded-full border px-4 py-2 text-sm font-semibold md:flex'>
                  Hi, {session.data.user.firstName ?? "there"}
                </div>
                <Button variant='ghost' asChild title='Account Menu'>
                  <User className={cn("text-icon-title-inv size-5 cursor-pointer md:hidden")} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className='md:w-60' align='end'>
                <DropdownMenuItem className='focus:bg-transparent'>
                  <div className='text-text-title block px-2 text-sm font-semibold md:hidden'>
                    Hi, {session.data.user.firstName ?? "there"}
                  </div>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className='hidden cursor-pointer focus:bg-transparent md:flex'>
                  <Link href='/profile-and-settings'>
                    <UserCircleIcon className='text-icon-title size-5 stroke-2' />
                    <DropdownMenuLabel>Profile & Settings</DropdownMenuLabel>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator className='hidden md:block' />
                <DropdownMenuItem asChild className='hidden cursor-pointer focus:bg-transparent md:flex'>
                  <Link href='/orders'>
                    <PackageBoxIcon className='text-icon-title size-5 stroke-2' />
                    <DropdownMenuLabel>My Orders</DropdownMenuLabel>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator className='hidden md:block' />
                <DropdownMenuItem asChild className='hidden cursor-pointer focus:bg-transparent md:flex'>
                  <Link href='/credits'>
                    <CircleStackIcon className='text-icon-title size-5 stroke-2' />
                    <DropdownMenuLabel>My Credits</DropdownMenuLabel>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className='hidden cursor-pointer focus:bg-transparent md:flex'>
                  <Link href='/notifications'>
                    <BellIcon className='text-icon-title size-5 stroke-2' />
                    <DropdownMenuLabel>Notifications</DropdownMenuLabel>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem className='cursor-pointer focus:bg-transparent' onClick={handleLogout}>
                  <LogoutIcon className='text-icon-title size-5 stroke-2' />
                  <DropdownMenuLabel>Log out</DropdownMenuLabel>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ) : (
          <>
            <div className={cn("hidden items-center gap-2 px-0 md:flex md:gap-4")}>
              <Button
                asChild
                variant='ghost'
                className={cn(
                  "text-text-title-inv dark:text-text-title px-0 text-xs font-semibold hover:bg-transparent md:text-sm 2xl:text-base dark:hover:bg-transparent",
                  isHomePage && "text-btn-primary"
                )}
              >
                <AnimatedLink
                  href={getMarketplaceUrl(`/register/${FEDERAL_WORKER_USERTYPE}/sign-up`)}
                  showHoverCircle={false}
                >
                  Sign Up
                </AnimatedLink>
              </Button>
              <Button
                asChild
                variant='ghost'
                className={cn(
                  "text-text-title-inv dark:text-text-title px-0 text-xs font-semibold hover:bg-transparent md:text-sm 2xl:text-base dark:hover:bg-transparent",
                  isHomePage && "text-btn-primary"
                )}
              >
                <AnimatedLink href={getMarketplaceUrl("/login")} showHoverCircle={false}>
                  Login
                </AnimatedLink>
              </Button>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant='ghost' asChild title='Account Menu'>
                  <User className={cn("text-icon-title-inv size-5 cursor-pointer md:hidden")} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className='md:w-60' align='end'>
                <DropdownMenuItem asChild className='cursor-pointer focus:bg-transparent'>
                  <Link href={getMarketplaceUrl(`/register/${FEDERAL_WORKER_USERTYPE}/sign-up`)}>Sign Up</Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild className='cursor-pointer focus:bg-transparent'>
                  <Link href={getMarketplaceUrl("/login")}>Login</Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        )}
      </div>
      {!isHomePage ? (
        <div className='w-full flex-[1_1_100%] md:hidden'>
          <SearchBar />
        </div>
      ) : null}
    </div>
  );
}
